// Per spec §3.2 + §3.3: POST /chat/session helper used during session bootstrap.

export interface ChatSessionRequest {
  vendor_id: string
  lead_email?: string
  lead_name?: string
  // Direction B "Ink Header" 4-field welcome: phone captured client-side,
  // normalized to E.164 server-side, stored on chat_sessions.lead_phone,
  // merged into text_conversation_details lead_info.lead_phone_number,
  // and written to followup_leads.lead_phone for SMS followup nudges.
  lead_phone?: string
  event_type?: string
  // A7: optional analytics signal — true on the cookie-autosubmit path
  // (device returner), distinct from the backend's text_conversation_details
  // GSI lookup (channel returner). Both flow into chat_sessions for split
  // analysis. The widget passes it only when the cookie was the source of
  // the autosubmit; first-time visitors and form-submit users omit it.
  is_returning_visitor?: boolean
}

export interface DomainOption {
  value: string
  display_name: string
}

export interface VendorInfo {
  business_name: string
  vendor_timezone: string
  // Direction B "Ink Header" handoff: agent name shown in the per-group meta
  // line ("MIKLA · AI AGENT") and the typing indicator ("MIKLA IS TYPING…").
  // Resolved server-side from voice_agent_config.agent_name ->
  // vendor_info_config.agent_identity.name -> "the assistant". Widget defaults
  // to "Mikla" when this is empty / "the assistant".
  agent_name?: string
  // Noun for the booking cards ("demo meeting"). null / missing = legacy "Tour".
  booking_noun?: string | null
  // Widget branding (chat_config.theme_color / logo_s3_key via the portal).
  // Null/absent -> stock Mikla accent + m. avatar.
  theme_color?: string | null
  logo_url?: string | null
}

export interface ChatSessionResponse {
  session_id: string
  thread_id: string
  coalesce_window_ms: number | null
  agent_domains: string[]
  domain_options: DomainOption[]
  // V1 opening message: server-rendered greeting shown as the first
  // assistant bubble before the user types. Tells the user the bot's
  // identity, capabilities, and prompts engagement. Per industry research
  // (Builtabot 2025: +40% engagement on value-focused greetings).
  opening_message?: string
  vendor_info: VendorInfo
  // Pre-chat form copy for packed (non-wedding) vendors. null / missing =
  // legacy vendor: the widget renders today's "Event type" / "Tell us about your event".
  domain_field_label?: string | null
  form_heading?: string | null
}

export async function postChatSession(
  apiBase: string,
  body: ChatSessionRequest,
  fetchImpl: typeof fetch = fetch
): Promise<ChatSessionResponse> {
  const res = await fetchImpl(`${apiBase}/chat/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(`chat/session failed: HTTP ${res.status}`)
  }
  return res.json() as Promise<ChatSessionResponse>
}
